import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { useComparisonMode } from './useComparisonMode';
import { useHumanAgentsQueryVariables } from './useHumanAgentsQueryVariables';
import { selectAnalyticsDates } from '../redux/selectors';
import { getComparisonDates } from '../utils/getComparisonDates';

export interface HumanAgentsOverviewData {
  total_conversations: number | null;
  avg_csat_human_agent: number | null;
  avg_resolution_seconds: number | null;
  avg_user_rating: number | null;
  user_rating_count: number | null;
  csat_human_agent_count: number | null;
}

interface QueryResponse {
  rows: HumanAgentsOverviewData[];
}

const OVERVIEW_FIELDS = `
  total_conversations
  avg_csat_human_agent
  avg_resolution_seconds
  avg_user_rating
  user_rating_count
  csat_human_agent_count
`;

export const GET_HUMAN_AGENTS_CONVERSATIONS_SUMMARY = gql`
  query HumanAgentsConversationsSummary(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_summary(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

// Distinct operation name so Apollo's cache treats current and comparison
// queries as separate (same shape, different time window).
const GET_HUMAN_AGENTS_CONVERSATIONS_SUMMARY_COMPARISON = gql`
  query HumanAgentsConversationsSummaryComparison(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $channel_types: _text
    $agent_ids: _text
    $csat_human_agent: _int4
    $min_num_user_messages: Int
    $is_test: Boolean
    $user_rating: _int4
  ) {
    rows: human_agents_conversations_summary(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        channel_types: $channel_types
        agent_ids: $agent_ids
        csat_human_agent: $csat_human_agent
        min_num_user_messages: $min_num_user_messages
        is_test: $is_test
        user_rating: $user_rating
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

export const useHumanAgentsOverview = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  const { variables, skip } = useHumanAgentsQueryVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useHumanAgentsQueryVariables(comparisonDates);

  const { loading, data, error } = useQuery<QueryResponse>(GET_HUMAN_AGENTS_CONVERSATIONS_SUMMARY, {
    variables,
    skip,
  });

  const {
    loading: comparisonLoading,
    data: comparisonData,
    error: comparisonError,
  } = useQuery<QueryResponse>(GET_HUMAN_AGENTS_CONVERSATIONS_SUMMARY_COMPARISON, {
    variables: comparisonVariables,
    skip: skip || !isComparing,
  });

  return {
    isLoading: loading || (isComparing && comparisonLoading),
    error: error ?? comparisonError,
    data: data?.rows?.[0] ?? null,
    comparisonData: comparisonData?.rows?.[0] ?? null,
  };
};
